import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { apiService } from '@/services/api';
import { toast } from '@/hooks/use-toast';
import { Clock, Coffee } from 'lucide-react';

interface WorkSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: number;
  userName: string;
  onSettingsUpdated?: () => void;
}

export function WorkSettingsDialog({
  open,
  onOpenChange,
  userId,
  userName,
  onSettingsUpdated
}: WorkSettingsDialogProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [breakMinutes, setBreakMinutes] = useState(60);
  const [workQuotaMinutes, setWorkQuotaMinutes] = useState(480);

  useEffect(() => {
    if (open && userId) {
      loadSettings();
    }
  }, [open, userId]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const settings = await apiService.getUserWorkSettings(userId);
      setBreakMinutes(settings.dailyBreakMinutes);
      setWorkQuotaMinutes(settings.dailyWorkQuotaMinutes);
    } catch (error) {
      console.error('Failed to load work settings:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca setările de lucru',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.updateUserWorkSettings(userId, {
        dailyBreakQuotaMinutes: breakMinutes,
        dailyWorkQuotaMinutes: workQuotaMinutes,
      });
      
      toast({
        title: 'Succes',
        description: `Setările de lucru pentru ${userName} au fost actualizate`,
      });
      
      onSettingsUpdated?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update work settings:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut actualiza setările de lucru',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatHours = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) return `${hours} ore`;
    return `${hours} ore ${mins} min`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Setări Lucru - {userName}</DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="py-4 text-center">Se încarcă...</div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="break" className="flex items-center gap-2">
                <Coffee className="h-4 w-4" />
                Pauză zilnică (minute)
              </Label>
              <Input
                id="break"
                type="number"
                min="0"
                max="240"
                value={breakMinutes}
                onChange={(e) => setBreakMinutes(parseInt(e.target.value) || 0)}
              />
              <p className="text-sm text-muted-foreground">
                {formatHours(breakMinutes)} - Timpul de pauză va fi adăugat automat la sfârșitul zilei
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="quota" className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Normă zilnică de lucru (minute)
              </Label>
              <Input
                id="quota"
                type="number"
                min="240"
                max="720"
                value={workQuotaMinutes}
                onChange={(e) => setWorkQuotaMinutes(parseInt(e.target.value) || 480)}
              />
              <p className="text-sm text-muted-foreground">
                {formatHours(workQuotaMinutes)} - Timpul minim de lucru pe zi
              </p>
            </div>
            
            <div className="rounded-lg bg-blue-50 p-3 text-sm">
              <p className="font-medium text-blue-900">Cum funcționează:</p>
              <ul className="mt-1 space-y-1 text-blue-700">
                <li>• Când lucrătorul oprește munca, timpul de pauză este adăugat automat</li>
                <li>• Timpul total înregistrat = timp lucrat efectiv + pauză</li>
                <li>• Norma zilnică reprezintă timpul minim de lucru expected</li>
              </ul>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Anulează
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Se salvează...' : 'Salvează'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}